import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MonitorPlay,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  Send,
  Pause,
  Play,
  Timer,
  Radio,
  Shield,
  TrendingUp,
  Eye,
  XCircle,
} from 'lucide-react';
import { liveExamMonitorApi, proctoringWsApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { ConfirmationModal } from '@azyntor-labs/nexus-ui';

interface ActiveExam {
  exam_id: string;
  title: string;
  active_students: number;
  completed_count: number;
  total_enrolled: number;
  violations: number;
  average_progress: number;
  status: string;
  start_time: string;
  end_time: string;
}

interface ActiveStudent {
  student_id: string;
  student_name: string;
  student_email: string;
  attempt_id: string;
  progress: number;
  time_remaining: number;
  status: string;
  violation_count: number;
  risk_level: string;
  started_at: string;
}

export const LiveExamMonitorPage = () => {
  const queryClient = useQueryClient();
  const [selectedExamId, setSelectedExamId] = useState('');
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [extendModal, setExtendModal] = useState(false);
  const [extendMinutes, setExtendMinutes] = useState(15);
  const [confirmPause, setConfirmPause] = useState(false);
  const [showConfirmTerminate, setShowConfirmTerminate] = useState(false);
  const [terminateStudentId, setTerminateStudentId] = useState<string | null>(null);

  const { data: activeExamsRes, isLoading: examsLoading } = useQuery({
    queryKey: ['active-exams'],
    queryFn: () => liveExamMonitorApi.getActiveExams(),
    refetchInterval: 10000,
  });

  const activeExams: ActiveExam[] = Array.isArray(activeExamsRes?.data?.data) ? (activeExamsRes.data.data as ActiveExam[]) : [];

  const { data: studentsRes } = useQuery({
    queryKey: ['active-students', selectedExamId],
    queryFn: () => liveExamMonitorApi.getActiveStudents(selectedExamId),
    enabled: !!selectedExamId,
    refetchInterval: 5000,
  });

  const students: ActiveStudent[] = Array.isArray(studentsRes?.data?.data) ? (studentsRes.data.data as ActiveStudent[]) : [];

  const { data: sessionRes } = useQuery({
    queryKey: ['exam-session', selectedExamId],
    queryFn: () => liveExamMonitorApi.getExamSession(selectedExamId),
    enabled: !!selectedExamId,
    staleTime: 10000,
  });

  const session = sessionRes?.data?.data as any;

  const broadcastMutation = useMutation({
    mutationFn: () => liveExamMonitorApi.sendBroadcast(selectedExamId, broadcastMessage),
    onSuccess: () => {
      toast.success('Broadcast sent');
      setBroadcastModal(false);
      setBroadcastMessage('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Broadcast failed'),
  });

  const extendMutation = useMutation({
    mutationFn: () => liveExamMonitorApi.extendTime(selectedExamId, { minutes: extendMinutes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-students'] });
      toast.success(`Time extended by ${extendMinutes} minutes`);
      setExtendModal(false);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to extend time'),
  });

  const pauseMutation = useMutation({
    mutationFn: () => liveExamMonitorApi.pauseExam(selectedExamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-exams'] });
      queryClient.invalidateQueries({ queryKey: ['exam-session'] });
      toast.success('Exam paused');
      setConfirmPause(false);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to pause'),
  });

  const resumeMutation = useMutation({
    mutationFn: () => liveExamMonitorApi.resumeExam(selectedExamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-exams'] });
      queryClient.invalidateQueries({ queryKey: ['exam-session'] });
      toast.success('Exam resumed');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to resume'),
  });

  const confirmTerminateSession = async () => {
    if (!terminateStudentId) return;
    try {
      await proctoringWsApi.terminateExam(terminateStudentId, {
        exam_id: selectedExamId,
        reason: 'Terminated by proctor',
      });
      toast.success('Session terminated');
      queryClient.invalidateQueries({ queryKey: ['active-students'] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to terminate session');
    }
    setShowConfirmTerminate(false);
    setTerminateStudentId(null);
  };

  const selectedExam = activeExams.find((e) => e.exam_id === selectedExamId);

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds) || seconds <= 0) return '0m 0s';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
  };

  const getRiskBadge = (level: string, count: number) => {
    if (level === 'high') return { bg: 'bg-red-500/15 border-red-500/25', text: 'text-red-400', label: `${count} violations` };
    if (level === 'medium') return { bg: 'bg-amber-500/15 border-amber-500/25', text: 'text-amber-400', label: `${count} violations` };
    return { bg: 'bg-emerald-500/10 border-emerald-500/20', text: 'text-emerald-400', label: count > 0 ? `${count}` : 'Clean' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white/90">Live Exam Monitor</h1>
          <p className="text-xs text-white/40 mt-0.5">Real-time monitoring of active exams</p>
        </div>
        <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
          <span className="text-xs font-medium text-emerald-400">LIVE</span>
        </div>
      </div>

      {/* Active Exam Cards */}
      {examsLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="spinner" />
        </div>
      ) : activeExams.length === 0 ? (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
            <MonitorPlay className="w-8 h-8 text-white/15" />
          </div>
          <p className="text-sm text-white/50">No active exams right now</p>
          <p className="text-xs text-white/30 mt-1">Exams will appear here when students start taking them</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {activeExams.map((exam) => {
            const isSelected = selectedExamId === exam.exam_id;
            return (
              <button
                key={exam.exam_id}
                onClick={() => setSelectedExamId(exam.exam_id)}
                className={`p-5 text-left rounded-xl transition-all border ${
                  isSelected
                    ? 'border-teal-500/40 bg-teal-500/[0.06] shadow-lg shadow-teal-500/5'
                    : 'border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12]'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white/90 leading-snug pr-2">{exam.title}</h3>
                  {isSelected && (
                    <Eye className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 rounded-md bg-teal-500/15 flex items-center justify-center">
                      <Users className="w-3.5 h-3.5 text-teal-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white/90">{exam.active_students}</p>
                      <p className="text-[10px] text-white/35">Active</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 rounded-md bg-emerald-500/15 flex items-center justify-center">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white/90">{exam.completed_count}</p>
                      <p className="text-[10px] text-white/35">Done</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 rounded-md bg-amber-500/15 flex items-center justify-center">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-amber-400">{exam.violations || 0}</p>
                      <p className="text-[10px] text-white/35">Violations</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-7 h-7 rounded-md bg-blue-500/15 flex items-center justify-center">
                      <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white/90">{(exam.average_progress || 0).toFixed(0)}%</p>
                      <p className="text-[10px] text-white/35">Progress</p>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Selected Exam Detail */}
      {selectedExamId && selectedExam && (
        <>
          {/* Summary Strip + Actions */}
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-5 py-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-6">
                <div>
                  <p className="text-sm font-semibold text-white/90">{selectedExam.title}</p>
                  <p className="text-xs text-white/40 mt-0.5">
                    {selectedExam.active_students} active &middot; {selectedExam.completed_count} completed &middot; {selectedExam.total_enrolled} enrolled
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setBroadcastModal(true)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-teal-500/15 text-teal-400 border border-teal-500/20 hover:bg-teal-500/25 transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Broadcast</span>
                </button>
                <button
                  onClick={() => setExtendModal(true)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-400 border border-blue-500/20 hover:bg-blue-500/25 transition-colors"
                >
                  <Timer className="w-3.5 h-3.5" />
                  <span>Extend Time</span>
                </button>
                {session?.status === 'paused' ? (
                  <button
                    onClick={() => resumeMutation.mutate()}
                    disabled={resumeMutation.isPending}
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/25 transition-colors"
                  >
                    <Play className="w-3.5 h-3.5" />
                    <span>Resume</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmPause(true)}
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/20 hover:bg-amber-500/25 transition-colors"
                  >
                    <Pause className="w-3.5 h-3.5" />
                    <span>Pause All</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Active Students Table */}
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
            <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-teal-400" />
                <h3 className="text-sm font-medium text-white/90">Active Students</h3>
                <span className="text-xs text-white/30">({students.length})</span>
              </div>
              <div className="flex items-center space-x-1.5 text-[10px] text-white/30">
                <Clock className="w-3 h-3" />
                <span>Auto-refreshes every 5s</span>
              </div>
            </div>
            {students.length === 0 ? (
              <div className="p-10 text-center">
                <Users className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="text-sm text-white/40">No active students</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-white/[0.02]">
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-white/40 uppercase tracking-wider">Student</th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-white/40 uppercase tracking-wider">Progress</th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-white/40 uppercase tracking-wider">Time Left</th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-white/40 uppercase tracking-wider">Status</th>
                      <th className="px-5 py-3 text-left text-[11px] font-medium text-white/40 uppercase tracking-wider">Violations</th>
                      <th className="px-5 py-3 text-right text-[11px] font-medium text-white/40 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {students.map((student) => {
                      const risk = getRiskBadge(student.risk_level, student.violation_count);
                      const timeLeft = student.time_remaining || 0;
                      const isLowTime = timeLeft > 0 && timeLeft < 300;
                      return (
                        <tr key={student.student_id} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500/20 to-blue-500/20 flex items-center justify-center text-xs font-bold text-white/70">
                                {(student.student_name || student.student_email || '?')[0].toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white/90">{student.student_name}</p>
                                <p className="text-[11px] text-white/35">{student.student_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center space-x-3">
                              <div className="w-28 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    student.progress >= 80 ? 'bg-emerald-500' :
                                    student.progress >= 40 ? 'bg-teal-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${Math.min(student.progress, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-white/70 w-10">{student.progress.toFixed(0)}%</span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className={`flex items-center space-x-1.5 ${isLowTime ? 'text-red-400' : 'text-white/70'}`}>
                              <Clock className="w-3.5 h-3.5" />
                              <span className={`text-sm font-medium ${isLowTime ? 'animate-pulse' : ''}`}>
                                {formatTime(timeLeft)}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-[11px] font-medium border ${
                              student.status === 'in_progress'
                                ? 'bg-teal-500/10 border-teal-500/20 text-teal-400'
                                : student.status === 'terminated'
                                ? 'bg-red-500/10 border-red-500/20 text-red-400'
                                : 'bg-white/[0.04] border-white/[0.06] text-white/50'
                            }`}>
                              {student.status === 'in_progress' && <Play className="w-3 h-3" />}
                              {student.status === 'terminated' && <XCircle className="w-3 h-3" />}
                              <span>{student.status === 'in_progress' ? 'In Progress' : student.status || 'Active'}</span>
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-[11px] font-medium border ${risk.bg}`}>
                              <Shield className="w-3 h-3" />
                              <span className={risk.text}>{risk.label}</span>
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={async () => {
                                  try {
                                    await proctoringWsApi.sendWarning(student.attempt_id, {
                                      warning_type: 'proctor_warning',
                                      message: 'Warning: Suspicious activity detected.',
                                      remaining: 2,
                                    });
                                    toast.success('Warning sent');
                                  } catch (err: any) {
                                    toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to send warning');
                                  }
                                }}
                                className="flex items-center space-x-1 px-2.5 py-1.5 text-[11px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-md hover:bg-amber-500/20 transition-colors"
                                title="Send Warning"
                              >
                                <AlertTriangle className="w-3 h-3" />
                                <span>Warn</span>
                              </button>
                              <button
                                onClick={() => {
                                  setTerminateStudentId(student.attempt_id);
                                  setShowConfirmTerminate(true);
                                }}
                                className="flex items-center space-x-1 px-2.5 py-1.5 text-[11px] font-medium bg-red-500/10 text-red-400 border border-red-500/20 rounded-md hover:bg-red-500/20 transition-colors"
                                title="Terminate Session"
                              >
                                <XCircle className="w-3 h-3" />
                                <span>End</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Broadcast Modal */}
      {broadcastModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setBroadcastModal(false)}>
          <div className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center">
                <Send className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white/90">Broadcast Message</h3>
                <p className="text-xs text-white/40">Send to all active students</p>
              </div>
            </div>
            <textarea
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              placeholder="Type your message..."
              className="input-dark w-full"
              rows={4}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button onClick={() => setBroadcastModal(false)} className="px-4 py-2 rounded-lg text-sm text-white/50 hover:text-white/80 transition-colors">Cancel</button>
              <button
                onClick={() => broadcastMutation.mutate()}
                disabled={!broadcastMessage.trim() || broadcastMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-40 transition-colors"
              >
                {broadcastMutation.isPending ? 'Sending...' : 'Send Broadcast'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Time Modal */}
      {extendModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setExtendModal(false)}>
          <div className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center">
                <Timer className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white/90">Extend Time</h3>
                <p className="text-xs text-white/40">Add minutes for all students</p>
              </div>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1.5">Additional minutes</label>
              <input type="number" value={extendMinutes} onChange={(e) => setExtendMinutes(Number(e.target.value))} className="input-dark w-full" min={1} />
            </div>
            <div className="flex justify-end space-x-3 mt-5">
              <button onClick={() => setExtendModal(false)} className="px-4 py-2 rounded-lg text-sm text-white/50 hover:text-white/80 transition-colors">Cancel</button>
              <button
                onClick={() => extendMutation.mutate()}
                disabled={extendMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 transition-colors"
              >
                {extendMutation.isPending ? 'Extending...' : `Extend ${extendMinutes}m`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pause Confirmation */}
      {confirmPause && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setConfirmPause(false)}>
          <div className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <Pause className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-base font-semibold text-white/90">Pause Exam?</h3>
            </div>
            <p className="text-sm text-white/50 mb-5 ml-[52px]">This will pause the exam for all active students. They cannot continue until you resume.</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmPause(false)} className="px-4 py-2 rounded-lg text-sm text-white/50 hover:text-white/80 transition-colors">Cancel</button>
              <button
                onClick={() => pauseMutation.mutate()}
                disabled={pauseMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition-colors"
              >
                {pauseMutation.isPending ? 'Pausing...' : 'Pause All'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmTerminate}
        onClose={() => { setShowConfirmTerminate(false); setTerminateStudentId(null); }}
        onConfirm={confirmTerminateSession}
        title="Terminate Exam Session?"
        message="Terminate this student's exam session?"
        variant="danger"
        confirmText="Terminate"
        cancelText="Cancel"
      />
    </div>
  );
};
